import { useExamples, useRunsQuery, useSessions } from '@/hooks/useSwr';
import { ExampleSchema, RunSchema, SessionSchema } from '@/types/schema';

/**
 * Process runs and examples to find valid outputs
 * Prioritizes examples in their original order and finds the first valid run for each
 * Maps all runs to their respective examples
 */
export function processRunsAndExamples(
  examples: ExampleSchema[],
  runs: RunSchema[],
  experiments: SessionSchema[]
): {
  processedRuns: { data: { runs: RunSchema[] } };
  runIdsToExample: Record<string, ExampleSchema | undefined>;
} {
  const runIdsToExample: Record<string, ExampleSchema | undefined> = {};

  // Create a map for sorting by experiment recency
  const experimentIndexMap = new Map(
    experiments.map((exp, index) => [exp.id, index])
  );

  // Sort all runs by experiment recency
  const sortedRuns = [...runs].sort((a, b) => {
    const aExpIndex = experimentIndexMap.get(a.session_id) ?? 999;
    const bExpIndex = experimentIndexMap.get(b.session_id) ?? 999;
    return aExpIndex - bExpIndex;
  });

  // Create a map of examples by ID for easy lookup
  const exampleMap = new Map(examples.map((example) => [example.id, example]));

  // Map all runs to their examples
  sortedRuns.forEach((run) => {
    const exampleId = run.reference_example_id;
    if (exampleId) {
      const example = exampleMap.get(exampleId);
      if (example) {
        runIdsToExample[run.id] = example;
      }
    }
  });

  // Find the first example that has a valid run for display purposes
  let priorityRun: RunSchema | undefined;

  for (const example of examples) {
    // Find all runs for this example, already sorted by recency
    const runsForExample = sortedRuns.filter(
      (run) => run.reference_example_id === example.id
    );

    // Find the first valid run for this example
    const validRun = runsForExample.find(
      (run) => run.outputs && Object.keys(run.outputs).length > 0 && !run.error
    );

    if (validRun) {
      // Found a valid run for an example
      priorityRun = validRun;
      break;
    }
  }

  // If a priority run was found, return just that run for display
  // but still include the full mapping of all runs to examples
  if (priorityRun) {
    return {
      processedRuns: { data: { runs: [priorityRun] } },
      runIdsToExample,
    };
  }

  // If no valid runs were found for any example, return all runs
  return {
    processedRuns: { data: { runs } },
    runIdsToExample,
  };
}

export const useRecentRunsAndExamples = ({
  datasetId,
  sessionId,
  variant = 'dataset',
  filters,
  select,
}: {
  datasetId?: string;
  sessionId?: string;
  variant?: 'dataset' | 'session';
  filters?: {
    filter?: string;
    traceFilter?: string;
    treeFilter?: string;
  };
  select?: string[];
}) => {
  // First, load examples from the dataset
  const examples = useExamples(
    datasetId ? { dataset: datasetId, limit: 10 } : null,
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  // Load recent experiments/sessions
  const recentExperiments = useSessions(
    datasetId
      ? {
          reference_dataset: [datasetId],
          limit: 5,
          sort_by: 'start_time',
          sort_by_desc: true,
          include_stats: false,
        }
      : null,
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  // Extract experiment IDs to query
  const experimentIds =
    recentExperiments.data?.rows?.map((exp) => exp.id).filter(Boolean) || [];

  const sessionToQuery = variant === 'session' ? sessionId : undefined;
  const sessionsToQuery = sessionToQuery ? [sessionToQuery] : experimentIds;

  const referenceExampleIds = examples.data?.map((e) => e.id);
  const filterWithRoot = filters?.filter
    ? `and(${filters?.filter}, eq(is_root, true))`
    : `eq(is_root, true)`;

  // Get runs from these sessions
  const recentRuns = useRunsQuery(
    sessionsToQuery.length === 0
      ? null
      : {
          session: sessionsToQuery,
          limit: 50, // Increase limit to have more chances of finding valid outputs
          reference_example: referenceExampleIds,
          filter: datasetId ? filterWithRoot : filters?.filter,
          trace_filter: filters?.traceFilter,
          tree_filter: filters?.treeFilter,
          skip_prev_cursor: true,
          select,
        },
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const isLoading =
    examples.isLoading || recentExperiments.isLoading || recentRuns.isLoading;

  // Initialize with empty values
  let processedData = {
    processedRuns: { data: { runs: [] as RunSchema[] } },
    runIdsToExample: {} as Record<string, ExampleSchema | undefined>,
  };

  // Process data if everything is loaded
  if (
    !isLoading &&
    examples.data &&
    recentRuns.data?.runs &&
    recentExperiments.data?.rows
  ) {
    processedData = processRunsAndExamples(
      examples.data,
      recentRuns.data.runs,
      recentExperiments.data.rows
    );
  }

  return {
    recentRuns: recentRuns,
    examples,
    recentExperiment: recentExperiments,
    runIdsToExample: processedData.runIdsToExample,
    isLoading,
  };
};

export const useDatasetRunsAndExamples = (datasetId?: string) => {
  // First, load examples from the dataset
  const examples = useExamples(
    datasetId ? { dataset: datasetId, limit: 10 } : null,
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  // Load recent experiments/sessions
  const recentExperiments = useSessions(
    datasetId
      ? {
          reference_dataset: [datasetId],
          limit: 5,
          sort_by: 'start_time',
          sort_by_desc: true,
          include_stats: false,
        }
      : null,
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  // Extract experiment IDs to query
  const experimentIds =
    recentExperiments.data?.rows?.map((exp) => exp.id).filter(Boolean) || [];

  const referenceExampleIds = examples.data?.map((e) => e.id);

  // Get runs from these sessions
  const recentRuns = useRunsQuery(
    experimentIds.length === 0
      ? null
      : {
          session: experimentIds,
          limit: 50, // Increase limit to have more chances of finding valid outputs
          reference_example: referenceExampleIds,
          filter: `eq(is_root, true)`,
          skip_prev_cursor: true,
        },
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const isLoading =
    examples.isLoading || recentExperiments.isLoading || recentRuns.isLoading;

  // Initialize with empty values
  let processedData = {
    processedRuns: { data: { runs: [] as RunSchema[] } },
    runIdsToExample: {} as Record<string, ExampleSchema | undefined>,
  };

  // Process data if everything is loaded
  if (
    !isLoading &&
    examples.data &&
    recentRuns.data?.runs &&
    recentExperiments.data?.rows
  ) {
    processedData = processRunsAndExamples(
      examples.data,
      recentRuns.data.runs,
      recentExperiments.data.rows
    );
  }

  return {
    recentRuns: recentRuns,
    examples,
    recentExperiment: recentExperiments,
    runIdsToExample: processedData.runIdsToExample,
    isLoading,
  };
};
